import {
  ACCOUNT_DESER,
  CUSTOMER_DESER,
  Customer,
  ItemsResponse,
  RED_FLAG_RECORD_PARSER,
  TRADING_STATUS_DESER,
  TradingStatus,
  parseErrorContainer,
  type Account,
  type JsonHelper,
  type JsonValue,
  type RedFlagRecord,
  type TwUser,
} from "../../../tastyworks"
import {
  ADMIN_CUSTOMER_ACCOUNT_PARSER,
  AUTHORITIES_DESER_PARSER,
  Authority,
  USER_DESER_PARSER,
  type AdminCustomerAccountParams,
  type CustomerAccountRowResult,
} from "../../../tastyworks/admin/admin-customer-account"
import type HttpClient from "../../../tastyworks/http"

export class AdminCustomerAccountService {
  private readonly adminCustomerAccountClient: HttpClient

  private readonly customersClient: HttpClient

  constructor(httpClient: HttpClient) {
    this.adminCustomerAccountClient = httpClient.nested("customers-accounts")
    this.customersClient = httpClient.nested("customers")
  }

  readonly index = async (
    params: AdminCustomerAccountParams
  ): Promise<ItemsResponse<CustomerAccountRowResult>> =>
    this.adminCustomerAccountClient
      .get("", params)
      .then((res) => this.populateAdminCustomersResult(res))

  readonly populateAdminCustomersResult = (
    helper: JsonHelper
  ): ItemsResponse<CustomerAccountRowResult> => {
    const response = new ItemsResponse<CustomerAccountRowResult>()

    parseErrorContainer(helper, response)

    if (!helper.hasField("data")) {
      return response
    }

    const data = helper.getChild("data")
    response.items = AdminCustomerAccountService.parseCustomerAccountsList(data)

    return response
  }

  readonly fetchRedFlags = async (
    customerId: string | undefined
  ): Promise<ItemsResponse<RedFlagRecord>> => {
    if (!customerId) {
      return new ItemsResponse<RedFlagRecord>()
    }

    return this.customersClient
      .nested(customerId)
      .indexSimple(RED_FLAG_RECORD_PARSER, "red-flag-records")
  }

  readonly fetchAccountsByCustomerId = async (
    customerId: string
  ): Promise<ItemsResponse<Account>> =>
    this.customersClient
      .nested(customerId)
      .indexSimple(ADMIN_CUSTOMER_ACCOUNT_PARSER, "accounts")

  static parseCustomerAccountsList = (
    data: JsonHelper
  ): CustomerAccountRowResult[] => {
    const accountNumtoAccount = new Map<JsonValue, Account>()

    const authIdToAuthority = new Map<JsonValue, Authority>()

    const allAuthorities = data.parseArray(
      "authorities",
      AUTHORITIES_DESER_PARSER
    )

    allAuthorities.forEach((authority) => {
      authIdToAuthority.set(authority.id, authority)
    })

    data.parseArray("accounts", (json) => {
      accountNumtoAccount.set(
        json.getString("account-number"),
        ACCOUNT_DESER.toParser()(json)
      )
    })

    const userIdToUser = new Map<string, TwUser>()
    const users = data.parseArray("users", USER_DESER_PARSER)
    const userIdtoCustomer = new Map<string, Customer>()
    users.forEach((user) => {
      userIdToUser.set(user.id, user)
    })

    const accountNumToTS = new Map<JsonValue, TradingStatus>()
    const tradingStatuses = data.parseArray(
      "trading-statuses",
      TRADING_STATUS_DESER.toParser(TradingStatus)
    )
    tradingStatuses.forEach((tradingStatus) => {
      accountNumToTS.set(tradingStatus.accountNumber, tradingStatus)
    })

    const customerAccountsList: CustomerAccountRowResult[] = []

    data.parseArray("customers", (json) => {
      const customer: Customer = CUSTOMER_DESER.toParser(Customer)(json)
      const authIds = json.getPrimitiveArray("account-authorities")
      const authorities: Authority[] = []
      authIds.forEach((id) => {
        authorities.push(authIdToAuthority.get(id) ?? new Authority())
      })
      const userId = json.getString("user-id")
      userIdtoCustomer.set(userId, customer)

      const accountNumbers = json.getPrimitiveArray("account-numbers")

      if (accountNumbers.length === 0) {
        customerAccountsList.push({
          customer,
          account: undefined,
          authorities: [],
          user: userIdToUser.get(String(userId)),
          tradingStatus: new TradingStatus(),
        })
      }

      accountNumbers.forEach((accountNumber) => {
        customerAccountsList.push({
          customer,
          account: accountNumtoAccount.get(accountNumber),
          authorities,
          user: userIdToUser.get(String(userId)),
          tradingStatus: accountNumToTS.get(accountNumber),
        })
      })
    })

    users.forEach((user) => {
      if (userIdtoCustomer.get(user.id) === undefined) {
        customerAccountsList.push({
          customer: new Customer(),
          account: undefined,
          authorities: [],
          user,
          tradingStatus: new TradingStatus(),
        })
      }
    })

    return customerAccountsList
  }
}
